import {
  useQuery,
  useInfiniteQuery,
  keepPreviousData,
} from '@tanstack/react-query';
import { productsApi } from '@/api/products.api';
import { categoriesApi } from '@/api/categories.api';
import { queryKeys } from './queryKeys';
import type { ProductFilters } from '@/types/api';

const ONE_MIN = 60_000;
const TWO_MIN = 2 * 60_000;
const FIVE_MIN = 5 * 60_000;

export function useProducts(filters: ProductFilters = {}) {
  return useInfiniteQuery({
    queryKey: queryKeys.products.list(filters),
    queryFn: ({ pageParam }) =>
      productsApi.list({ ...filters, page: pageParam as number }),
    initialPageParam: filters.page ?? 1,
    getNextPageParam: (last) => last.pagination.nextPage,
    staleTime: TWO_MIN,
    placeholderData: keepPreviousData,
  });
}

export function useFeaturedProducts(limit = 8) {
  return useQuery({
    queryKey: [...queryKeys.products.featured, limit],
    queryFn: () => productsApi.featured(limit),
    staleTime: FIVE_MIN,
  });
}

export function useNewArrivals(limit = 8) {
  return useQuery({
    queryKey: [...queryKeys.products.all, 'new-arrivals', limit] as const,
    queryFn: () => productsApi.list({ sort: 'newest', limit }),
    staleTime: FIVE_MIN,
    select: (r) => r.items,
  });
}

export function useProduct(slug: string | undefined) {
  return useQuery({
    queryKey: queryKeys.products.detail(slug ?? ''),
    queryFn: () => productsApi.getBySlug(slug!),
    staleTime: FIVE_MIN,
    enabled: !!slug,
  });
}

export function useProductSearch(query: string, debouncedQuery: string) {
  return useQuery({
    queryKey: queryKeys.products.search(debouncedQuery),
    queryFn: () => productsApi.search(debouncedQuery),
    enabled: debouncedQuery.trim().length >= 2,
    staleTime: ONE_MIN,
    placeholderData: keepPreviousData,
    // Render an empty state for the typed-but-not-yet-debounced query so
    // the UI stays responsive while the user is mid-typing.
    select: (data) => ({
      ...data,
      isStale: query !== debouncedQuery,
    }),
  });
}

export function useCategories() {
  return useQuery({
    queryKey: queryKeys.categories.all,
    queryFn: () => categoriesApi.getTree(),
    staleTime: 10 * 60_000, // categories rarely change
  });
}

export function useCategory(slug: string | undefined) {
  return useQuery({
    queryKey: queryKeys.categories.detail(slug ?? ''),
    queryFn: () => categoriesApi.getBySlug(slug!),
    staleTime: 10 * 60_000,
    enabled: !!slug,
  });
}
